// レポート選択・作成モーダルコンポーネント

import { useState, useEffect } from 'react';
import { Report } from '../../../types/report.types';
import * as styles from '../../styles/components/graph-builder.css.ts';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';

interface ReportModalProps {
  isOpen: boolean;
  reports: Report[];
  mode: 'select' | 'create';
  selectedReportId: string | null;
  onModeChange: (mode: 'select' | 'create') => void;
  onReportSelect: (reportId: string) => void;
  onConfirm: (data: { reportId?: string; newReportName?: string; newReportDescription?: string }) => void;
  onClose: () => void;
}

export function ReportModal({
  isOpen,
  reports,
  mode,
  selectedReportId,
  onModeChange,
  onReportSelect,
  onConfirm,
  onClose
}: ReportModalProps) {
  // 内部でstateを管理（親の再レンダリングを防ぐ）
  const [newReportName, setNewReportName] = useState('');
  const [newReportDescription, setNewReportDescription] = useState('');

  // モーダルが閉じられたらリセット
  useEffect(() => {
    if (!isOpen) {
      setNewReportName('');
      setNewReportDescription('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (mode === 'select') {
      onConfirm({ reportId: selectedReportId || undefined });
    } else {
      onConfirm({
        newReportName: newReportName.trim(),
        newReportDescription: newReportDescription.trim()
      });
    }
  };
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add to Report"
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={
              (mode === 'select' && !selectedReportId) ||
              (mode === 'create' && !newReportName.trim())
            }
          >
            {mode === 'select' ? 'Add to Selected Report' : 'Create and Add'}
          </Button>
        </>
      }
    >
      <div>
        {/* タブ切り替え（レポートが存在する場合のみ表示） */}
        {reports.length > 0 && (
          <div className={styles.modalTabs}>
            <button
              className={`${styles.modalTab} ${mode === 'select' ? styles.active : ''}`}
              onClick={() => onModeChange('select')}
            >
              Select Existing Report
            </button>
            <button
              className={`${styles.modalTab} ${mode === 'create' ? styles.active : ''}`}
              onClick={() => onModeChange('create')}
            >
              Create New Report
            </button>
          </div>
        )}
        
        {/* 既存レポート選択モード */}
        {mode === 'select' && (
          <div className={styles.reportList}>
            {reports.map(report => (
              <label key={report.id} className={styles.reportItem}>
                <input
                  type="radio"
                  name="report"
                  value={report.id}
                  checked={selectedReportId === report.id}
                  onChange={(e) => onReportSelect(e.target.value)}
                />
                <div className={styles.reportInfo}>
                  <div className={styles.reportName}>{report.name}</div>
                  {report.description && (
                    <div className={styles.reportDescription}>{report.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
        
        {/* 新規レポート作成モード */}
        {mode === 'create' && (
          <div className={styles.createReportForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Report Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={newReportName}
                onChange={(e) => setNewReportName(e.target.value)}
                placeholder="Enter report name"
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Description (Optional)</label>
              <textarea
                className={styles.formTextarea}
                value={newReportDescription}
                onChange={(e) => setNewReportDescription(e.target.value)}
                placeholder="Enter report description"
                rows={3}
              />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}